import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import SeoHead from "../components/site/SeoHead";
import { PriceBook, PriceBookDrawer } from "../components/admin/PriceBook";
import { LeadsTab } from "../components/admin/LeadsTab";
import { ImportPdfDialog } from "../components/admin/ImportPdfDialog";
import { PaymentsPanel } from "../components/admin/PaymentsPanel";
import { AutoGrowTextarea } from "../components/admin/AutoGrowTextarea";
import { DocumentLineItem, editorInputClass } from "../components/admin/DocumentLineItem";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const TOKEN_KEY = "vlx_admin_token";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "");

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("quotes");
  const [editing, setEditing] = useState(null); // { kind, doc, draft } or null
  const [importing, setImporting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const signOut = useCallback((msg) => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEditing(null);
    if (msg) toast.error(msg);
  }, []);

  useEffect(() => {
    if (!token) return;
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401 && err.config?.url?.startsWith(API) && !err.config.url.endsWith("/login") && localStorage.getItem(TOKEN_KEY)) {
          signOut("Session expired — please sign in again");
        }
        return Promise.reject(err);
      },
    );
    axios.get(`${API}/me`, { headers: auth() }).catch(() => {});
    return () => axios.interceptors.response.eject(id);
  }, [token, signOut]);

  if (!token) return (
    <>
      <SeoHead title="ViewLux Admin" description="Restricted access." noindex />
      <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />
    </>
  );

  return (
    <div data-testid="admin-page" className="min-h-screen bg-cream font-nunito text-[#0f2420]">
      <SeoHead title="ViewLux Admin" description="Restricted access." noindex />
      <header className="bg-[#0f2420] text-white px-6 py-4 flex items-center justify-between">
        <h1 className="text-lg font-cormorant italic tracking-wide">ViewLux Admin</h1>
        <button
          data-testid="admin-logout"
          onClick={() => signOut()}
          className="text-[11px] tracking-[0.3em] uppercase font-raleway text-[#e2b880] hover:text-white"
        >Sign out</button>
      </header>
      <nav className="border-b border-[#c99658]/25 px-4 sm:px-6 flex flex-wrap items-center gap-x-4 sm:gap-x-6 bg-white">
        {["quotes", "estimates", "invoices", "pricebook"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`py-3 whitespace-nowrap text-[12px] tracking-[0.3em] uppercase font-raleway border-b-2 ${
              tab === t ? "border-[#c99658] text-[#0f2420]" : "border-transparent text-neutral-600 hover:text-[#7a5b2f]"
            }`}
          >{t === "pricebook" ? "Price Book" : t === "quotes" ? "Leads" : t}</button>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {(tab === "estimates" || tab === "invoices") && (
            <>
            <button
              data-testid="btn-import-pdf"
              onClick={() => setImporting(true)}
              className="my-2 px-4 py-2 whitespace-nowrap border border-[#c99658] text-[#0f2420] text-[11px] tracking-[0.3em] uppercase font-raleway hover:bg-[#c99658] transition-colors"
            >Import PDF</button>
            <button
              data-testid={`btn-new-${tab.slice(0, -1)}`}
              onClick={() => setEditing({ kind: tab.slice(0, -1), doc: null })}
              className="my-2 px-4 py-2 whitespace-nowrap bg-[#c99658] text-[#0f2420] text-[11px] tracking-[0.3em] uppercase font-raleway hover:bg-[#7a5b2f] hover:text-white transition-colors"
            >+ New {tab.slice(0, -1)}</button>
            </>
          )}
        </div>
      </nav>
      <main className={tab === "pricebook" ? "max-w-6xl mx-auto h-[calc(100vh-110px)]" : "p-6 max-w-6xl mx-auto"}>
        {tab === "pricebook" && <PriceBook />}
        {tab === "quotes" && <LeadsTab key={`q-${reloadKey}`} onConvert={() => { setTab("estimates"); setReloadKey((k) => k + 1); }} />}
        {tab === "estimates" && <DocsTab key={`e-${reloadKey}`} kind="estimate" onEdit={(d) => setEditing({ kind: "estimate", doc: d })} />}
        {tab === "invoices" && <DocsTab key={`i-${reloadKey}`} kind="invoice" onEdit={(d) => setEditing({ kind: "invoice", doc: d })} />}
      </main>
      {importing && (
        <ImportPdfDialog
          defaultKind={tab === "estimates" ? "estimate" : "invoice"}
          onClose={() => setImporting(false)}
          onImported={(kind, draft) => { setImporting(false); setTab(`${kind}s`); setEditing({ kind, doc: null, draft }); }}
        />
      )}
      {editing && (
        <Editor
          kind={editing.kind}
          initial={editing.doc}
          draft={editing.draft}
          onClose={() => setEditing(null)}
          onSaved={(opts) => { if (!opts?.keepOpen) setEditing(null); setReloadKey((k) => k + 1); }}
          onConverted={(inv) => { setTab("invoices"); setReloadKey((k) => k + 1); setEditing({ kind: "invoice", doc: inv }); }}
        />
      )}
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("admin@viewluxcolorado.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/login`, { email, password });
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-[#0f2420] flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-cream p-8 rounded-sm">
        <h2 className="text-2xl font-cormorant italic text-[#0f2420] mb-1">ViewLux Admin</h2>
        <p className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] mb-6">Restricted access</p>
        <label className="block text-[10.5px] tracking-[0.3em] uppercase font-raleway text-neutral-600 mb-1">Email</label>
        <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full border border-neutral-300 px-3 py-2 mb-4 bg-white text-sm outline-none focus:border-[#c99658]" />
        <label className="block text-[10.5px] tracking-[0.3em] uppercase font-raleway text-neutral-600 mb-1">Password</label>
        <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full border border-neutral-300 px-3 py-2 mb-6 bg-white text-sm outline-none focus:border-[#c99658]" />
        <button data-testid="login-submit" disabled={busy} className="w-full py-3 bg-[#c99658] text-[#0f2420] text-[11px] tracking-[0.3em] uppercase font-raleway hover:bg-[#7a5b2f] hover:text-white disabled:opacity-50">{busy ? "Signing in…" : "Sign In"}</button>
      </form>
    </div>
  );
}

function DocsTab({ kind, onEdit }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${kind}s`, { headers: auth() });
      setDocs(data);
    } catch (e) { if (e.response?.status !== 401) toast.error(`Failed to load ${kind}s`); }
    setLoading(false);
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.number}?`)) return;
    try {
      await axios.delete(`${API}/${kind}s/${d.id}`, { headers: auth() });
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const convert = async (d) => {
    try {
      await axios.post(`${API}/estimates/${d.id}/convert`, {}, { headers: auth() });
      toast.success("Converted to invoice");
      load();
    } catch { toast.error("Convert failed"); }
  };

  const setStatus = async (d, status) => {
    const amt = status === "deposit_paid" ? d.deposit_amount : (d.balance_due ?? d.total);
    if (!window.confirm(`Record ${status === "deposit_paid" ? "deposit" : "payment"} of ${money(amt)} on ${d.number}?`)) return;
    try {
      await axios.post(`${API}/invoices/${d.id}/status`, { status }, { headers: auth() });
      toast.success(status === "deposit_paid" ? `Deposit of ${money(amt)} recorded` : "Marked paid in full");
      load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <p className="text-neutral-600">Loading…</p>;

  return (
    <div className="min-w-0">
      <table data-testid={`${kind}s-table`} className="block md:table w-full text-sm">
        <thead className="hidden md:table-header-group text-left text-[10.5px] tracking-[0.3em] uppercase font-raleway text-neutral-600 border-b">
          <tr><th className="py-2">Number</th><th>Customer</th><th>Date</th><th>Total</th><th>Status</th><th></th></tr>
        </thead>
        <tbody className="block md:table-row-group">
          {docs.map((d) => (
            <tr key={d.id} className="grid grid-cols-2 gap-3 md:table-row py-4 md:py-0 border-b hover:bg-white/60">
              <td className="min-w-0 break-words md:py-3 font-mono text-xs">{d.number}</td>
              <td className="min-w-0 [overflow-wrap:anywhere]">{d.customer?.name}</td>
              <td className="min-w-0 break-words">{fmtDate(d.created_at)}</td>
              <td className="min-w-0 break-words">{money(d.total)}{kind === "invoice" && d.amount_paid > 0 && d.balance_due > 0 && <span className="block text-[11px] text-[#7a5b2f]">due {money(d.balance_due)}</span>}</td>
              <td className="min-w-0 col-span-2 break-words"><span data-testid={`status-${d.id}`} className={`text-[10.5px] tracking-[0.2em] uppercase font-raleway ${d.status === "paid" ? "text-emerald-700" : "text-[#7a5b2f]"}`}>{d.status?.replace("_", " ")}</span></td>
              <td className="min-w-0 col-span-2 flex flex-wrap gap-3 md:table-cell md:text-right text-[11px] tracking-[0.2em] uppercase font-raleway md:space-x-3">
                <button data-testid={`edit-${d.id}`} onClick={() => onEdit(d)} className="hover:text-[#c99658]">Edit</button>
                {kind === "estimate" && (
                  <button data-testid={`convert-${d.id}`} onClick={() => convert(d)} className="hover:text-[#c99658]">{d.status === "accepted" ? "Invoice again" : "→ Invoice"}</button>
                )}
                {kind === "invoice" && d.status !== "paid" && (
                  <>
                    {!(d.amount_paid > 0) && <button data-testid={`deposit-${d.id}`} onClick={() => setStatus(d, "deposit_paid")} className="hover:text-[#c99658]">Deposit Paid</button>}
                    <button data-testid={`paid-${d.id}`} onClick={() => setStatus(d, "paid")} className="hover:text-[#c99658]">Paid</button>
                  </>
                )}
                <button data-testid={`delete-${d.id}`} onClick={() => remove(d)} className="hover:text-red-600">Delete</button>
              </td>
            </tr>
          ))}
          {!docs.length && <tr><td colSpan={6} className="py-6 text-neutral-600">No {kind}s yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Editor({ kind, initial, draft, onClose, onSaved, onConverted }) {
  const isNew = !initial;
  const [payments, setPayments] = useState(initial?.payments || []);
  const [form, setForm] = useState(() => initial || (draft && {
    customer: { name: "", email: "", phone: "", company: "", address: "", ...Object.fromEntries(Object.entries(draft.customer || {}).map(([k, v]) => [k, v || ""])) },
    line_items: draft.line_items.length ? draft.line_items.map((li) => ({ ...li, image_url: li.image_url || null })) : [{ description: "", quantity: 1, unit_price: 0, image_url: null }],
    tax_percent: draft.tax_percent || 0,
    deposit_percent: 50,
    notes: [draft.source_number ? `Imported from ${draft.source_number}` : "", draft.notes || ""].filter(Boolean).join("\n"),
  }) || {
    customer: { name: "", email: "", phone: "", company: "", address: "" },
    line_items: [{ description: "", quantity: 1, unit_price: 0, image_url: null }],
    tax_percent: 0,
    deposit_percent: 50,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [showPriceBook, setShowPriceBook] = useState(false);

  const addFromPriceBook = (item) => {
    const li = { quantity: 1, ...item };
    setForm((f) => {
      const items = f.line_items;
      const last = items[items.length - 1];
      const blank = last && !last.description && !Number(last.unit_price);
      return { ...f, line_items: blank ? [...items.slice(0, -1), li] : [...items, li] };
    });
    toast.success(`Added · ${money(item.unit_price)}`);
  };

  const uploadImage = async (idx, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, fd, {
        headers: { ...auth(), "Content-Type": "multipart/form-data" },
      });
      patchItem(idx, "image_url", data.file_url);
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
  };

  const downloadPdf = async () => {
    if (isNew) { toast.error("Save first"); return; }
    try {
      const res = await axios.get(`${API}/${kind}s/${initial.id}/pdf`, {
        headers: auth(), responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ViewLux-${kind === "estimate" ? "Estimate" : "Invoice"}-${initial.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const totals = useMemo(() => {
    const subtotal = form.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
    const tax = subtotal * (Number(form.tax_percent) || 0) / 100;
    const total = subtotal + tax;
    const deposit = total * (Number(form.deposit_percent) || 0) / 100;
    return { subtotal, tax, total, deposit };
  }, [form]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const patchCust = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const patchItem = (i, k, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, j) => j === i ? { ...li, [k]: v } : li) }));
  const addItem = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", quantity: 1, unit_price: 0, image_url: null }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const blank = (v) => (typeof v === "string" && v.trim() === "" ? null : v);
      const payload = {
        customer: {
          name: form.customer.name,
          email: blank(form.customer.email),
          phone: blank(form.customer.phone),
          company: blank(form.customer.company),
          address: blank(form.customer.address),
        },
        line_items: form.line_items.map((li) => ({
          description: li.description,
          quantity: Number(li.quantity) || 0,
          unit_price: Number(li.unit_price) || 0,
          image_url: li.image_url || null,
        })),
        tax_percent: Number(form.tax_percent) || 0,
        deposit_percent: Number(form.deposit_percent) || 0,
        notes: blank(form.notes),
      };
      if (isNew) {
        await axios.post(`${API}/${kind}s`, payload, { headers: auth() });
      } else {
        await axios.patch(`${API}/${kind}s/${initial.id}`, payload, { headers: auth() });
      }
      toast.success(`${kind === "estimate" ? "Estimate" : "Invoice"} saved`);
      (onSaved || onClose)();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setBusy(false); }
  };

  const sendEmail = async () => {
    if (isNew) { toast.error("Save first"); return; }
    const to = window.prompt("Send to email:", form.customer.email || "");
    if (!to) return;
    try {
      await axios.post(`${API}/${kind}s/${initial.id}/send`, { to }, { headers: auth() });
      toast.success("Sent");
    } catch { toast.error("Send failed"); }
  };

  const toInvoice = async () => {
    if (!window.confirm(`Create an invoice from ${initial.number}? Unsaved edits here won't be included.`)) return;
    try {
      const { data } = await axios.post(`${API}/estimates/${initial.id}/convert`, {}, { headers: auth() });
      toast.success(`Invoice ${data.number} created`);
      onConverted?.(data);
    } catch { toast.error("Convert failed"); }
  };

  const paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div data-testid="editor-modal" className="fixed inset-0 z-[2000] bg-[#0a1a17]/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="editor-title" className="w-full min-w-0 max-w-6xl bg-cream shadow-2xl flex flex-col max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-2rem)]">
        <div className="bg-[#0f2420] text-white px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#e2b880]">{isNew ? "New" : initial.number}</div>
            <h3 id="editor-title" data-testid="editor-title" className="font-cormorant italic text-2xl">{kind === "estimate" ? "Estimate" : "Invoice"}</h3>
          </div>
          <button data-testid="editor-close" aria-label="Close editor" onClick={onClose} className="w-11 h-11 text-[#e2b880] hover:text-white">✕</button>
        </div>
        <div data-testid="editor-body" className="p-4 sm:p-6 lg:p-8 space-y-8 overflow-y-auto flex-1 min-h-0 overscroll-contain">
          <section>
            <h4 className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] mb-3">Customer</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid="cust-name" aria-label="Customer name" placeholder="Name *" value={form.customer.name} onChange={(e) => patchCust("name", e.target.value)} className={editorInputClass} />
              <input data-testid="cust-email" aria-label="Customer email" placeholder="Email" value={form.customer.email || ""} onChange={(e) => patchCust("email", e.target.value)} className={editorInputClass} />
              <input data-testid="cust-phone" aria-label="Customer phone" placeholder="Phone" value={form.customer.phone || ""} onChange={(e) => patchCust("phone", e.target.value)} className={editorInputClass} />
              <input data-testid="cust-company" aria-label="Customer company" placeholder="Company" value={form.customer.company || ""} onChange={(e) => patchCust("company", e.target.value)} className={editorInputClass} />
              <AutoGrowTextarea data-testid="cust-address" aria-label="Customer address" placeholder="Address" rows={2} value={form.customer.address || ""} onChange={(e) => patchCust("address", e.target.value)} className="md:col-span-2" />
            </div>
          </section>
          <section>
            <h4 className="text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] mb-3">Line Items</h4>
            <div className="space-y-4">
              {form.line_items.map((li, i) => (
                <DocumentLineItem key={i} item={li} index={i} onPatch={(key, value) => patchItem(i, key, value)} onRemove={() => removeItem(i)} onUpload={file => uploadImage(i, file)} />
              ))}
              <div className="flex items-center gap-5">
                <button data-testid="add-line-item" onClick={addItem} className="text-[11px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] hover:text-[#0f2420]">+ Add item</button>
                <button data-testid="open-price-book" onClick={() => setShowPriceBook(true)} className="px-3 py-1.5 border border-[#c99658] text-[11px] tracking-[0.3em] uppercase font-raleway text-[#0f2420] hover:bg-[#c99658] transition-colors">Price Book</button>
              </div>
            </div>
          </section>
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] mb-1">Tax %</label>
              <input data-testid="tax-percent" aria-label="Tax percent" type="number" step="0.01" value={form.tax_percent} onChange={(e) => patch("tax_percent", e.target.value)} className={editorInputClass} />
              <label className="block text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] mb-1 mt-4">Deposit %</label>
              <input data-testid="deposit-percent" aria-label="Deposit percent" type="number" step="0.01" value={form.deposit_percent} onChange={(e) => patch("deposit_percent", e.target.value)} className={editorInputClass} />
            </div>
            <div className="bg-white border border-[#c99658]/25 p-4 text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="total-subtotal">{money(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span>Tax</span><span data-testid="total-tax">{money(totals.tax)}</span></div>
              <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1"><span>Total</span><span data-testid="total-total">{money(totals.total)}</span></div>
              {kind === "invoice" && paid > 0 ? (
                <>
                  <div className="flex justify-between text-[#7a5b2f]"><span>Paid</span><span data-testid="total-paid">−{money(paid)}</span></div>
                  <div className="flex justify-between font-semibold text-base border-t pt-1 mt-1"><span>{totals.total - paid <= 0.009 ? "Paid in full" : "Balance due"}</span><span data-testid="total-balance">{money(Math.max(totals.total - paid, 0))}</span></div>
                </>
              ) : (
                <div className="flex justify-between text-[#7a5b2f]"><span>Deposit{kind === "invoice" ? " due" : ""}</span><span data-testid="total-deposit">{money(totals.deposit)}</span></div>
              )}
            </div>
          </section>
          {kind === "invoice" && !isNew && (
            <PaymentsPanel invoiceId={initial.id} payments={payments} total={totals.total} depositAmount={totals.deposit}
              onChange={(doc) => { setPayments(doc.payments || []); onSaved?.({ keepOpen: true }); }} />
          )}
          <section>
            <label className="block text-[10.5px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] mb-1">Notes</label>
            <AutoGrowTextarea data-testid="editor-notes" aria-label="Notes" value={form.notes || ""} onChange={(e) => patch("notes", e.target.value)} />
          </section>
        </div>
        <div className="bg-[#0f2420]/5 px-6 py-4 flex justify-between items-center flex-wrap gap-3 shrink-0 border-t border-[#c99658]/25">
          {!isNew && (
            <div className="flex items-center gap-4 flex-wrap">
              <button data-testid="editor-send" onClick={sendEmail} className="text-[11px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] hover:text-[#0f2420]">Email to customer</button>
              <button data-testid="editor-pdf" onClick={downloadPdf} className="text-[11px] tracking-[0.3em] uppercase font-raleway text-[#7a5b2f] hover:text-[#0f2420]">Download PDF</button>
              {kind === "estimate" && (
                <button data-testid="editor-to-invoice" onClick={toInvoice} className="px-3 py-1.5 border border-[#c99658] text-[11px] tracking-[0.3em] uppercase font-raleway text-[#0f2420] hover:bg-[#c99658] transition-colors">→ Make Invoice</button>
              )}
            </div>
          )}
          <div className="ml-auto space-x-3">
            <button data-testid="editor-cancel" onClick={onClose} className="px-4 py-2 text-[11px] tracking-[0.3em] uppercase font-raleway text-neutral-600 hover:text-[#0f2420]">Cancel</button>
            <button data-testid="editor-save" disabled={busy} onClick={save} className="px-6 py-2 bg-[#c99658] text-[#0f2420] text-[11px] tracking-[0.3em] uppercase font-raleway hover:bg-[#7a5b2f] hover:text-white disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
      <PriceBookDrawer open={showPriceBook} onAdd={addFromPriceBook} onClose={() => setShowPriceBook(false)} />
    </div>
  );
}
